/**
 * Channel Auto-Import Policy (TV1-2354, extended to order history in TV1-2379)
 *
 * Fires the imports a channel was connected with — its existing PUBLICATIONS,
 * its past ORDER history, or both — one batch process each, once.
 *
 * ── Why this is deferred instead of inline ────────────────────────────────
 *
 * The obvious implementation — call the import right after the connect submit —
 * only works for one of the three connect paths. `AddChannelModal` branches on
 * `panelKind`:
 *
 *   - directProvision: mints `integrationUserId` itself, so the id IS known.
 *   - apiKey:          the SERVER validates the credentials and provisions;
 *                      the FE never sees the id.
 *   - oauth:           hands off to an external tab entirely, and the callback
 *                      provisions much later, out of this page's control.
 *
 * There is no moment in the modal where all three have an integration to import
 * from. What they DO share is that the finished channel eventually arrives
 * through the `ExternalSystemsIntegrations` subscription that `channelsSyncPolicy`
 * already runs. So the operator's choice is parked in `pendingChannelImports`,
 * and this policy watches for the channel to turn up.
 *
 * ── Matching, and why it is by connector rather than by id ────────────────
 *
 * At tick time the only stable thing known about the future channel is its
 * CONNECTOR. So a pending entry is keyed by connectorId, and the first channel
 * to appear for that connector claims it — then the entry is cleared, which is
 * what keeps this one-shot.
 *
 * The consequence, stated plainly: connect two channels of the SAME connector in
 * one session with the box ticked, and the first to arrive consumes the intent.
 * Acceptable — the alternative is correlating an OAuth callback to the click that
 * started it, which needs state this feature does not justify, and the manual
 * import in the channel detail covers the miss.
 *
 * That tradeoff is unchanged by carrying two kinds. The entry is consumed
 * WHOLESALE on arrival — both kinds at once, in one `clearPending` without a
 * `kind` — so a connector can never end up half-consumed, with (say) orders
 * still armed and re-firing against the second channel to arrive.
 *
 * Only channels that were ABSENT before are considered, so re-subscribing or a
 * snapshot replay cannot re-import a channel that was already there.
 *
 * ── Independence of the two commands ──────────────────────────────────────
 *
 * Each kind gets its OWN try/catch, not one around the pair. They are separate
 * batch processes with separate failure modes — the order import is rejected
 * outright by connectors that cannot count their order history — and a channel
 * whose publications imported fine must not lose that because the order seed
 * 400'd. The single-command version of this policy already swallowed-and-logged
 * for the same reason (a failed start is visible as a failed batch process); two
 * commands only make the isolation load-bearing rather than incidental.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { uuidv7 } from 'uuidv7';
import { canImportOrderHistory } from '../../../shared/state/capabilityGates';
import { ChannelsState } from '../state';
import { ChannelsCommand } from '../commands';

/**
 * Nothing to import from a retail store — it is a physical point of sale with no
 * channel-side listing catalog. `ChannelInfo` hides the manual import for it too.
 */
const CONNECTORS_WITHOUT_PUBLICATIONS = new Set(['retailstore']);

export const createChannelAutoImportPolicy = (
  backend: ClientLibraryType
): Policy<ChannelsState, ChannelsCommand> => ({
  name: 'channels-auto-import',

  shouldReact: ( prev, next ) => {
    if ( Object.keys( next.pendingChannelImports ).length === 0 ) return false;
    // Only when a channel actually appeared — this policy is about arrival, not
    // about every edit to an existing channel.
    return Object.keys( next.salesChannels ).length > Object.keys( prev.salesChannels ).length;
  },

  onStateChange: async ({ prev, next, ctx }) => {
    const arrived = Object.values( next.salesChannels ).filter(
      ( channel ) => !prev.salesChannels[channel.id]
    );

    for ( const [ connectorId, intent ] of Object.entries( next.pendingChannelImports )) {
      const channel = arrived.find(( c ) => c.connectorId === connectorId );
      if ( !channel ) continue;

      // Clear FIRST, and clear the WHOLE entry. The dispatch is synchronous while
      // the commands below are not, so clearing up front means a second channel
      // arriving mid-flight cannot match the same entry and start a duplicate
      // import — for either kind.
      ctx.dispatch(
        { type: 'integrations/channels/autoImport/clearPending', payload: { connectorId }},
        { source: 'api' }
      );

      if ( intent.publications && !CONNECTORS_WITHOUT_PUBLICATIONS.has( connectorId )) {
        // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- operator log only; a failed start surfaces as a failed batch process, and the channel connected fine (TV1-2354).
        try {
          // No `channelPublicationIds` — "import everything", the same body
          // `ChannelInfo`'s import modal sends for its `all` mode.
          await backend.commands.StartChannelPublicationImportBatchProcess({
            id: uuidv7(),
            body: {
              connectorId: channel.connectorId,
              integrationUserId: channel.integrationUserId
            }
          } as any );
        } catch ( error ) {
          console.error( '[channels] Auto-import of publications failed to start for', connectorId, error );
        }
      }

      // The order seed is scoped to connectors whose client can count the history
      // it is about to pull. The UI does not offer it elsewhere, so reaching this
      // with an unsupported connector means a stale parked intent — skip rather
      // than send a request the aggregate rejects with
      // BATCH_PROCESSES_CHANNEL_CAPABILITY_UNSUPPORTED.
      if ( intent.orders && canImportOrderHistory( connectorId )) {
        // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- operator log only; a failed start surfaces as a failed batch process, and the channel connected fine (TV1-2379).
        try {
          // No date window, by design: seeding imports the channel's FULL past
          // order history. See `ChannelOrderImportBatchProcessStartedEventSchema`.
          //
          // TEMPORARY CAST (TV1-2379) — the backend route exists and is in
          // `.openapi.json`, but the client library has NOT been regenerated, so
          // `StartChannelOrderImportBatchProcess` is absent from
          // `ClientLibraryType` and the PROPERTY ACCESS, not just the body, fails
          // to typecheck. Hence `backend.commands as any` rather than the sibling
          // publication call's trailing `} as any )`. After `yarn build &&
          // yarn generate-all`, delete this cast and call it plainly.
          await ( backend.commands as any ).StartChannelOrderImportBatchProcess({
            id: uuidv7(),
            body: {
              connectorId: channel.connectorId,
              integrationUserId: channel.integrationUserId
            }
          });
        } catch ( error ) {
          console.error( '[channels] Auto-import of order history failed to start for', connectorId, error );
        }
      }
    }
  }
});
